import { useState } from 'react';

import {
  Badge,
  Divider,
  Text,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  useColorModeValue,
  Collapse,
  Button,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiChevronDown, BiChevronUp, BiCalendar } from 'react-icons/bi';
import { FiHelpCircle, FiPieChart } from 'react-icons/fi';
import { HiOutlineDocumentReport } from 'react-icons/hi';

import { useCanCompare, useCompare, useRouteDatasetRef } from 'components/shared/Compare';
import AppCard from 'components/UI/AppCard';
import { Activity } from 'hooks/data/search/types';
import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { activityTypeCopy, activityTypes } from '../ProductSearchResultCard/ProductSearchResultTable/constants';
import { TableViewOptions } from '../SearchPreferencesModalContent/types';

import ActivitySearchResultTable from './ActivitySearchResultTable';

type Props = {
  activity: Activity;
  tableViewOption: TableViewOptions | undefined;
  resultPosition: number;
  isFirstResult?: boolean;
};

const ActivitySearchResultCard = ({ activity, tableViewOption, resultPosition, isFirstResult }: Props) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const leftBorderColor = useColorModeValue('green.400', 'green.600');
  const iconColor = useColorModeValue('green.500', 'green.300');

  const { isSelected } = useCompare();
  const { ver } = useVersionInfo();
  const canCompare = useCanCompare();
  const routeRef = useRouteDatasetRef();
  const isCardSelected =
    canCompare(ver) &&
    activity.products.some((product) => product.datasets.some((dataset) => isSelected(routeRef(dataset.id))));

  const calculatedTotalDatasets = activity.products.reduce((acc, product) => acc + product.total_datasets, 0);
  const { isOpen: timePeriodIsOpen, onClose: onTimePeriodClose, onToggle: onTimePeriodToggle } = useDisclosure();
  const { isOpen: activityIsOpen, onClose: onActivityClose, onToggle: onActivityToggle } = useDisclosure();

  const [expanded, setExpanded] = useState<boolean>();
  const shouldBeCollapsed = (tableViewOption ?? TableViewOptions.EXPANDED) !== TableViewOptions.EXPANDED;
  const actualExpanded = expanded ?? !shouldBeCollapsed;

  const closeActivityPopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Activity Type' });
    onActivityClose();
  };

  const closeTimePeriodPopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Time Period' });
    onTimePeriodClose();
  };

  return (
    <AppCard
      display="flex"
      flexDirection="column"
      alignItems="space-between"
      py={4}
      px={4}
      align="center"
      borderLeftColor={leftBorderColor}
      borderLeftWidth="8px"
      {...(isCardSelected && { borderColor: 'green.400', borderWidth: '2px' })}
      {...(isFirstResult && { 'data-tour-target': 'search-results-group' })}
    >
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" alignItems={{ base: 'stretch', md: 'center' }}>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          alignItems={{ base: 'stretch', md: 'center' }}
          spacing={5}
          px={4}
          w="100%"
        >
          <Stack spacing={1} w="100%" maxW={{ base: '100%', lg: '300px' }}>
            <Heading size="md">{activity.name}</Heading>
            <Badge variant="solid" colorScheme="green" alignSelf="flex-start">
              Activity
            </Badge>
          </Stack>
          <Divider display={{ base: 'none', md: 'block' }} orientation="vertical" w="3px" color="gray" h="80px" />
          <Stack w="100%" justify="center" fontSize={'sm'}>
            <HStack>
              <Icon as={HiOutlineDocumentReport} />
              <Text color={grayText}>{calculatedTotalDatasets} datasets</Text>
            </HStack>
            <HStack>
              <Icon as={FiPieChart} />
              <Text color={grayText}>{activityTypes[activity.activity_type]}</Text>
              <Popover isOpen={activityIsOpen} onClose={closeActivityPopover} strategy="fixed">
                <PopoverTrigger>
                  <Stack cursor={'pointer'}>
                    <Icon
                      as={FiHelpCircle}
                      color={iconColor}
                      onClick={() => {
                        mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Activity Type' });
                        onActivityToggle();
                      }}
                    />
                  </Stack>
                </PopoverTrigger>
                <PopoverContent px={6} py={5}>
                  <PopoverArrow />
                  <PopoverHeader borderBottom="none" p={0}>
                    <Heading size="sm">{activityTypes[activity.activity_type]}</Heading>
                  </PopoverHeader>
                  <PopoverBody p={0} mt={4}>
                    {activityTypeCopy[activity.activity_type]?.text}
                  </PopoverBody>
                  <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                    <Button width={{ base: '100%', md: 'auto' }} onClick={onActivityClose} variant="ghost">
                      Close
                    </Button>
                    {activityTypeCopy[activity.activity_type]?.link && (
                      <Button
                        width={{ base: '100%', md: 'auto' }}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Activity Type' });
                          onActivityClose();
                        }}
                        as={Link}
                        isExternal
                        href={activityTypeCopy[activity.activity_type]?.link}
                        sx={{ '&:hover': { textDecoration: 'none' } }}
                      >
                        Learn More
                      </Button>
                    )}
                  </Stack>
                </PopoverContent>
              </Popover>
            </HStack>
            <HStack>
              <Icon as={FiPieChart} />
              <Text color={grayText}>{activity.sectors.join(', ')}</Text>
            </HStack>
            {activity.time_period !== undefined && (
              <HStack>
                <Icon as={BiCalendar} />
                <Text color={grayText}>{activity.time_period}</Text>
                <Popover isOpen={timePeriodIsOpen} onClose={closeTimePeriodPopover} strategy="fixed">
                  <PopoverTrigger>
                    <Stack cursor={'pointer'}>
                      <Icon
                        as={FiHelpCircle}
                        color={iconColor}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Time Period' });
                          onTimePeriodToggle();
                        }}
                      />
                    </Stack>
                  </PopoverTrigger>
                  <PopoverContent px={6} py={5}>
                    <PopoverArrow />
                    <PopoverHeader borderBottom="none" p={0}>
                      <Heading size="sm">Time Period</Heading>
                    </PopoverHeader>
                    <PopoverBody p={0} mt={4}>
                      <Stack spacing={3}>
                        <Text>
                          The time period reflects the original data collection or extrapolation period, not an expiry date. A
                          dataset may have a past end date but remain valid if technologies and supply chains have not
                          significantly changed.
                        </Text>
                      </Stack>
                    </PopoverBody>
                    <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                      <Button width={{ base: '100%', md: 'auto' }} onClick={onTimePeriodClose} variant="ghost">
                        Close
                      </Button>
                      <Button
                        width={{ base: '100%', md: 'auto' }}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Time Period' });
                          onTimePeriodClose();
                        }}
                        as={Link}
                        isExternal
                        href={
                          'https://support.ecoinvent.org/activities-products#Activities:~:text=of%20Europe%20(RER).-,Its%20Time%20Period%C2%A0,-The%20time%20period'
                        }
                        sx={{ '&:hover': { textDecoration: 'none' } }}
                      >
                        Learn More
                      </Button>
                    </Stack>
                  </PopoverContent>
                </Popover>
              </HStack>
            )}
          </Stack>
        </Stack>
        <HStack>
          <IconButton
            width={'100%'}
            onClick={() => setExpanded(!actualExpanded)}
            aria-label="Expand"
            icon={actualExpanded ? <BiChevronUp /> : <BiChevronDown />}
            colorScheme="green"
            variant="subtle"
          />
        </HStack>
      </Stack>
      <Collapse in={actualExpanded} unmountOnExit>
        <ActivitySearchResultTable activity={activity} resultPosition={resultPosition} />
      </Collapse>
    </AppCard>
  );
};

export default ActivitySearchResultCard;
